/**
 * Comments, made visible to the parser.
 *
 * ProseMirror's parser only ever visits element and text nodes, so a comment
 * never reaches rule matching at all — `<!--[if mso]><table>…</table><![endif]-->`
 * is gone before any rule could claim it, and with it the Outlook-only layout
 * the designer wrote it for.
 *
 * Rewriting each comment into an element on the HTML *string*, before the
 * parser sees it, is what gives `htmlRaw` something to match. The comment is
 * put back verbatim on render.
 *
 * One known limitation: a comment between two `<tr>`s becomes a `<div>` in a
 * place the HTML parser will not keep it, and gets foster-parented out of the
 * table. It survives — it just moves. Inside a cell, or around a whole table,
 * which is where designers actually write these, nothing moves.
 */
import { inlineStylesheets } from "./inline-css";
import { sanitizeForEmail } from "./sanitize";

export const RAW_COMMENT_ATTRIBUTE = "data-raw-comment";

/** Regions whose content is text, where a comment is not really a comment. */
const TEXT_CONTENT_ELEMENT = /<(style|script)\b[^>]*>[\s\S]*?<\/\1\s*>/gi;

const COMMENT = /<!--([\s\S]*?)-->/g;

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function rewriteComments(html: string): string {
  return html.replace(
    COMMENT,
    (_match, body: string) =>
      `<div ${RAW_COMMENT_ATTRIBUTE}="${escapeAttribute(body)}"></div>`,
  );
}

/**
 * The marker `convertToReactEmail` puts on the scaffold it wraps every email in.
 */
const CONTAINER_SELECTOR = "[data-email-container] > tbody > tr > td";

/**
 * Unwrap our own send scaffold, and make what is left safe to keep.
 *
 * Reopening a saved draft parses the HTML the composer sent, and that HTML is
 * wrapped in the `<Body>`/`<Container>` tables the send renderer adds. Parsing
 * those as content gives the document an extra row and cell each time it is
 * opened, with the clinic's style settings frozen into it as if the author had
 * typed them. The document is the source of truth, and this is the scaffold
 * being unwrapped rather than mistaken for it.
 *
 * Sanitising happens in the same pass, because this is the door: everything
 * downstream — the canvas, the preview, the sent email — is generated from the
 * document, so markup that never gets in never has to be stripped again.
 */
function readForIngest(html: string): string {
  if (typeof DOMParser === "undefined") return html;
  // Wrapped in `<body>` — the same trick Tiptap's own parser uses — because
  // document parsing hoists a leading `<style>` into `<head>`, and a template's
  // stylesheet is usually the first thing in the file. Parsed in a body
  // context it stays where the designer put it. A full `<html>` document works
  // too: the parser drops the inner tags and keeps the content, which is how a
  // stylesheet written in `<head>` reaches the document at all.
  const document = new DOMParser().parseFromString(
    `<body>${html}</body>`,
    "text/html",
  );
  const root =
    (html.includes("data-email-container") &&
      document.querySelector(CONTAINER_SELECTOR)) ||
    document.body;
  // Before sanitising, not after: this writes new style attributes, and a
  // check that ran first would not have seen what it wrote.
  inlineStylesheets(root);
  sanitizeForEmail(root);
  return root.innerHTML;
}

/**
 * Prepare foreign markup for `generateJSON` / `setContent`.
 *
 * The one door into the document: a loaded template, a reopened draft, a paste.
 * It unwraps our own scaffold, strips what can execute, and makes comments
 * visible to a parser that would otherwise never see them.
 */
export function prepareEmailHtml(source: string): string {
  const html = readForIngest(source);
  let out = "";
  let index = 0;
  TEXT_CONTENT_ELEMENT.lastIndex = 0;
  for (
    let match = TEXT_CONTENT_ELEMENT.exec(html);
    match;
    match = TEXT_CONTENT_ELEMENT.exec(html)
  ) {
    out += rewriteComments(html.slice(index, match.index)) + match[0];
    index = match.index + match[0].length;
  }
  return out + rewriteComments(html.slice(index));
}
